import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getAuth, User } from 'firebase/auth';
import {
  collection,
  getDocs,
  getFirestore,
  query,
  where,
  DocumentData,
  QueryDocumentSnapshot,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  arrayUnion,
  deleteDoc,
  arrayRemove
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  CustomUserData,
  Organisation,
  UserOrganisationsRoles,
  UserInfo,
  UserOrganisationsByEmail,
  CustomTableUsers,
  CustomerSubscription
} from './interfaces';

export const firebaseConfig = {
  apiKey: 'AIzaSyBqjfCNBEZwll3wkkiH6b2kyHo64kqUwoA',
  authDomain: 'enzo-prod.firebaseapp.com',
  projectId: 'enzo-prod',
  storageBucket: 'enzo-prod.appspot.com',
  messagingSenderId: '668672164406',
  appId: '1:668672164406:web:e9519c4feed2730d974e97',
  measurementId: 'G-781H80NMTV'
};

export const app = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);
export const auth = getAuth(app);
export const firestore = getFirestore(app);
export const functions = getFunctions(app, 'europe-west3');

export const organisationConverter = {
  toFirestore(org: Organisation): DocumentData {
    return { ...org };
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): Organisation {
    let org = snapshot.data()! as Organisation;
    org.id = snapshot.id;
    return org;
  }
};

export const userInfoConverter = {
  toFirestore(user: UserInfo): DocumentData {
    return { ...user };
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): UserInfo {
    let user = snapshot.data()! as UserInfo;
    return user;
  }
};

console.debug('Firebase initialized');

export const getUsers = async (): Promise<CustomUserData[]> => {
  const users: CustomUserData[] = [];
  const querySnapshot = await getDocs(collection(firestore, 'userData'));
  querySnapshot.forEach((doc) => {
    users.push({
      id: doc.id,
      uid: doc.data().uid,
      name: doc.data().personalInfo.name,
      email: doc.data().personalInfo.email,
      createdAt: doc.data().createdAt
    });
  });
  return users;
};

export const getCompleteUsersInfo = async (): Promise<UserInfo[]> => {
  const usersCollection = await getDocs(collection(firestore, 'userData').withConverter(userInfoConverter));
  return usersCollection.docs.map((doc) => doc.data());
};

export const getCompleteUsersInfoByOrganisations = async (orgIds: string[]): Promise<UserInfo[]> => {
  const _allUsersEmails = await Promise.all(
    orgIds.map(async (id) => {
      const docRef = doc(firestore, 'organisationsData', id);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) return [...snapshot.data().members, ...snapshot.data().admins];
      return [];
    })
  );
  const uniqueEmails = [...new Set(_allUsersEmails.flat(1))];
  const _allUsers = await Promise.all(
    uniqueEmails.map(async (email) => {
      const usersCollectionQuery = query(
        collection(firestore, 'userData'),
        where('personalInfo.email', '==', email)
      ).withConverter(userInfoConverter);
      const snapshot = await getDocs(usersCollectionQuery);
      if (snapshot && snapshot.docs.length > 0) {
        return snapshot.docs[0].data();
      }
      return undefined;
    })
  );

  return _allUsers.filter((el): el is UserInfo => !!el);
};

export const getUsersEmails = async (): Promise<string[]> => {
  const users: string[] = [];
  const querySnapshot = await getDocs(collection(firestore, 'userData'));
  querySnapshot.forEach((doc) => {
    users.push(doc.data().personalInfo.email);
  });
  return users;
};

export const getUserOrganisationsRoles = async (user: User): Promise<UserOrganisationsRoles> => {
  const superAdminDoc = await getDoc(doc(firestore, 'admins/superAdmins'));
  const isSuperAdmin = user.email ? (superAdminDoc.data() as { emails: string[] }).emails.includes(user.email) : false;

  const memberQuery = query(collection(firestore, 'organisationsData'), where('members', 'array-contains', user.email));
  const memberQueryWithConverter = memberQuery.withConverter(organisationConverter);
  const memberInDocs = await getDocs(memberQueryWithConverter);
  const memberIn = memberInDocs.docs.map((d) => d.data());

  const adminQuery = query(collection(firestore, 'organisationsData'), where('admins', 'array-contains', user.email));
  const adminQueryWithConverter = adminQuery.withConverter(organisationConverter);
  const adminInDocs = await getDocs(adminQueryWithConverter);
  const adminIn = adminInDocs.docs.map((d) => d.data());

  return { isSuperAdmin, adminIn, memberIn };
};

export const getUserOrganisationsByEmail = async (email: string): Promise<UserOrganisationsByEmail> => {
  const memberQuery = query(collection(firestore, 'organisationsData'), where('members', 'array-contains', email));
  const memberQueryWithConverter = memberQuery.withConverter(organisationConverter);
  const memberInDocs = await getDocs(memberQueryWithConverter);
  const memberIn = memberInDocs.docs.map((d) => ({ orgName: d.data().name, orgId: d.id }));

  const memberQuery2 = query(collection(firestore, 'organizations'), where('email', 'array-contains', email));

  const memberInDocs2 = await getDocs(memberQuery2);
  const memberIn2 = memberInDocs2.docs.map((d) => ({ orgName: d.data().name, orgId: d.id }));

  const adminQuery = query(collection(firestore, 'organisationsData'), where('admins', 'array-contains', email));
  const adminQueryWithConverter = adminQuery.withConverter(organisationConverter);
  const adminInDocs = await getDocs(adminQueryWithConverter);
  const adminIn = adminInDocs.docs.map((d) => ({ orgName: d.data().name, orgId: d.id }));

  return memberIn.concat(adminIn, memberIn2);
};

export const getAllOrganisations = async () => {
  const orgs = await getDocs(collection(firestore, 'organisationsData').withConverter(organisationConverter));
  return orgs.docs.map((doc) => doc.data());
};

export const getOrganisationById = async (id: string): Promise<Organisation | undefined> => {
  const docRef = doc(firestore, 'organisationsData', id);
  const snapshot = await getDoc(docRef);
  if (snapshot.exists()) return { ...snapshot.data(), id: snapshot.id } as Organisation;
  return undefined;
};

export const createOrganisation = async (payload: Partial<Organisation>) => {
  return await addDoc(collection(firestore, 'organisationsData'), payload);
};

export const updateOrganisation = async (payload: Partial<Organisation>, orgId: string) => {
  await updateDoc(doc(firestore, 'organisationsData', orgId), { ...payload });
  const snapshot = await getDoc(doc(firestore, 'organisationsData', orgId));
  if (snapshot.exists()) return { ...snapshot.data(), id: snapshot.id } as Organisation;
  return undefined;
};

export const getOrganisationByPromoCode = async (promoCode: string): Promise<Organisation | undefined> => {
  const promoCodeQuery = query(collection(firestore, 'organisationsData'), where('promoCode', '==', promoCode));
  const promoCodeQueryWithConverter = promoCodeQuery.withConverter(organisationConverter);
  const orgDocs = await getDocs(promoCodeQueryWithConverter);
  if (orgDocs.docs && orgDocs.docs.length > 0) return orgDocs.docs[0].data();
  return undefined;
};

export const addMemberToOrganisation = async (email: string, orgId: string) => {
  const docRef = doc(firestore, 'organisationsData', orgId);
  return await updateDoc(docRef, { members: arrayUnion(email) });
};

export const deleteOrganisationById = async (orgId: string) => {
  const docRef = doc(firestore, 'organisationsData', orgId);
  return await deleteDoc(docRef);
};

export const getStripeSubscriptionByUserId = async (id: string): Promise<CustomerSubscription | null> => {
  const collectionRef = collection(firestore, 'customers', id, 'subscriptions');
  const docRef = doc(firestore, 'customers', id);
  const snapshot = await getDocs(collectionRef);
  if (snapshot.docs.length > 0) {
    const doc = await getDoc(docRef);
    return { ...doc.data(), subscriptions: snapshot.docs.map((doc) => doc.data()) } as CustomerSubscription;
  }
  return null;
};

export const deleteUsersByID = async (users: CustomTableUsers[]) => {
  return await Promise.all(
    users.map(async (user) => {
      /*     const userReflectionsQuery = query(collection(firestore, 'reflections'), where('uid', '==', user.uid));
    const reflectionsSnapShot = await getDocs(userReflectionsQuery)
    reflectionsSnapShot.docs.forEach(async doc=>{
      await deleteDoc(doc.ref)
    }) */
      const oldMemberQuery = query(
        collection(firestore, 'organizations'),
        where('email', 'array-contains', user.email)
      );
      const oldMemberSnapshot = await getDocs(oldMemberQuery);
      oldMemberSnapshot.docs.forEach(async (doc) => {
        await updateDoc(doc.ref, { email: arrayRemove(user.email) });
      });
      const memberQuery = query(
        collection(firestore, 'organisationsData'),
        where('members', 'array-contains', user.email)
      );
      const memberSnapshot = await getDocs(memberQuery);
      memberSnapshot.docs.forEach(async (doc) => {
        await updateDoc(doc.ref, { members: arrayRemove(user.email) });
      });
      const adminQuery = query(
        collection(firestore, 'organisationsData'),
        where('admins', 'array-contains', user.email)
      );
      const adminSnapshot = await getDocs(adminQuery);
      adminSnapshot.docs.forEach(async (doc) => {
        await updateDoc(doc.ref, { admins: arrayRemove(user.email) });
      });
      /*    const userDataRef = doc(firestore,"userData",user.uid)
    await deleteDoc(userDataRef) */
      const callable = httpsCallable(functions, 'deleteUserDataAndReflections');
      await callable();
    })
  );
};
